import * as THREE from "three";
import { GPUComputationRenderer } from "three/addons/misc/GPUComputationRenderer.js";
import Orchestrator from "../Orchestrator.js";
import vertexShader from "./shaders/gaussianSplats.vert.glsl";
import fragmentShader from "./shaders/gaussianSplats.frag.glsl";
import gpgpuParticlesShader from "./shaders/gpgpu/particles.glsl";

export default class GaussianSplats {
    constructor() {
        this.orc = new Orchestrator();
        this.scene = this.orc.scene;
        this.resources = this.orc.resources;
        this.debug = this.orc.debug;
        this.camera = this.orc.camera;
        this.sizes = this.orc.sizes;
        this.renderer = this.orc.renderer.instance;

        this.splatData = this.resources.items.splat;

        this.params = {
            size: 0.1,
            positionZ: 4.5,
            flowFieldInfluence: 0.2,
            flowFieldStrength: 1.5,
            flowFieldFrequency: 2.0,
        };

        this.setGpgpu();
        this.setGeometry();
        this.setMaterial();
        this.setPoints();
        this.configureCameraProjection();
        this.setDebug();
    }

    setGpgpu() {
        this.gpgpu = {};
        this.gpgpu.count = this.splatData.count;
        this.gpgpu.size = Math.ceil(Math.sqrt(this.gpgpu.count));
        this.gpgpu.computation = new GPUComputationRenderer(
            this.gpgpu.size,
            this.gpgpu.size,
            this.renderer,
        );

        const baseParticlesTexture = this.gpgpu.computation.createTexture();
        const data = baseParticlesTexture.image.data;

        for (let i = 0; i < this.gpgpu.count; i++) {
            const i3 = i * 3;
            const i4 = i * 4;

            data[i4 + 0] = this.splatData.positions[i3 + 0];
            data[i4 + 1] = this.splatData.positions[i3 + 1];
            data[i4 + 2] = this.splatData.positions[i3 + 2];
            data[i4 + 3] = Math.random();
        }

        this.gpgpu.particlesVariable = this.gpgpu.computation.addVariable(
            "uParticles",
            gpgpuParticlesShader,
            baseParticlesTexture,
        );
        this.gpgpu.computation.setVariableDependencies(
            this.gpgpu.particlesVariable,
            [this.gpgpu.particlesVariable],
        );

        const uniforms = this.gpgpu.particlesVariable.material.uniforms;
        uniforms.uTime = new THREE.Uniform(0);
        uniforms.uDeltaTime = new THREE.Uniform(0);
        uniforms.uBase = new THREE.Uniform(baseParticlesTexture);
        uniforms.uFlowFieldInfluence = new THREE.Uniform(
            this.params.flowFieldInfluence,
        );
        uniforms.uFlowFieldStrength = new THREE.Uniform(
            this.params.flowFieldStrength,
        );
        uniforms.uFlowFieldFrequency = new THREE.Uniform(
            this.params.flowFieldFrequency,
        );

        this.gpgpu.computation.init();
    }

    setGeometry() {
        const particlesUvArray = new Float32Array(this.gpgpu.count * 2);
        const sizesArray = new Float32Array(this.gpgpu.count);

        for (let y = 0; y < this.gpgpu.size; y++) {
            for (let x = 0; x < this.gpgpu.size; x++) {
                const i = y * this.gpgpu.size + x;
                if (i >= this.gpgpu.count) {
                    break;
                }

                const i2 = i * 2;
                particlesUvArray[i2 + 0] = (x + 0.5) / this.gpgpu.size;
                particlesUvArray[i2 + 1] = (y + 0.5) / this.gpgpu.size;
                sizesArray[i] = Math.random();
            }
        }

        this.geometry = new THREE.BufferGeometry();
        this.geometry.setDrawRange(0, this.gpgpu.count);
        this.geometry.setAttribute(
            "aParticlesUv",
            new THREE.BufferAttribute(particlesUvArray, 2),
        );
        this.geometry.setAttribute(
            "color",
            new THREE.BufferAttribute(this.splatData.colors, 3),
        );
        this.geometry.setAttribute(
            "aSize",
            new THREE.BufferAttribute(sizesArray, 1),
        );

        const box = new THREE.Box3().setFromArray(this.splatData.positions);
        this.boundingSphere = box.getBoundingSphere(new THREE.Sphere());
    }

    setMaterial() {
        this.material = new THREE.ShaderMaterial({
            uniforms: {
                uSize: { value: this.params.size },
                uPixelRatio: { value: this.sizes.pixelRatio },
                uParticlesTexture: { value: null },
            },
            vertexShader,
            fragmentShader,
            transparent: true,
            depthWrite: true,
            vertexColors: true,
        });
    }

    setPoints() {
        this.points = new THREE.Points(this.geometry, this.material);
        this.points.position.z = this.params.positionZ;
        this.points.frustumCulled = false;
        this.scene.add(this.points);
    }

    configureCameraProjection() {
        const { radius } = this.boundingSphere;

        this.camera.instance.far = Math.max(500, radius * 10);
        this.camera.instance.updateProjectionMatrix();
    }

    setDebug() {
        if (this.debug.active) {
            this.debugFolder = this.debug.ui.addFolder("gaussianSplats");

            this.debugFolder
                .add(this.params, "size")
                .min(0.01)
                .max(2)
                .step(0.01)
                .onChange((value) => {
                    this.material.uniforms.uSize.value = value;
                });

            this.debugFolder
                .add(this.params, "positionZ")
                .name("position Z")
                .min(-200)
                .max(200)
                .step(0.1)
                .onChange((value) => {
                    this.points.position.z = value;
                });

            this.debugFolder
                .add(this.params, "flowFieldInfluence")
                .name("flow influence")
                .min(0)
                .max(1)
                .step(0.001)
                .onChange((value) => {
                    this.gpgpu.particlesVariable.material.uniforms.uFlowFieldInfluence.value =
                        value;
                });

            this.debugFolder
                .add(this.params, "flowFieldStrength")
                .name("flow strength")
                .min(0)
                .max(10)
                .step(0.001)
                .onChange((value) => {
                    this.gpgpu.particlesVariable.material.uniforms.uFlowFieldStrength.value =
                        value;
                });

            this.debugFolder
                .add(this.params, "flowFieldFrequency")
                .name("flow frequency")
                .min(0)
                .max(10)
                .step(0.001)
                .onChange((value) => {
                    this.gpgpu.particlesVariable.material.uniforms.uFlowFieldFrequency.value =
                        value;
                });
        }
    }

    update(elapsed, delta) {
        const uniforms = this.gpgpu.particlesVariable.material.uniforms;
        uniforms.uTime.value = elapsed;
        uniforms.uDeltaTime.value = delta;

        this.gpgpu.computation.compute();

        this.material.uniforms.uParticlesTexture.value =
            this.gpgpu.computation.getCurrentRenderTarget(
                this.gpgpu.particlesVariable,
            ).texture;
        this.material.uniforms.uPixelRatio.value = this.sizes.pixelRatio;
    }
}
